
import { MathUtil } from "@base/math/MathUtil";
import { gtc, ltc } from "@base/util/Comma";
import { appTemplates } from "@biz/definitions/ArticleShopTemplates";
import { FL } from "@biz/definitions/FL";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { INetData, NetEyeletType } from "@biz/items/net/NetDefinitions";
import { NetGeometryHelper } from "@biz/items/net/NetGeometryHelper";
import { NetLogic } from "@biz/items/net/NetLogic";
import { ILocaContext } from "@biz/language/LocaContext";
import { ClothData } from "@biz/logic/ClothData";
import { Measure } from "@biz/logic/Measure";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { PD } from "@biz/PD";
import { ICalcContext, ICalculatedProductResult, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

interface IArticleWithCount {
  articleNumber: string;
  count: number;
}

function calcEyeletArticle(eyeletType: NetEyeletType): string {
  switch (eyeletType) {
    case "galvanized":
      return "KONFEYEG16S";
    case "stainless":
      return "KONFEYES12S";
  }
  throw new Error("EyeletType not supported: ", eyeletType);
}

function calculateEyeletArticles(product: INetData): IArticleWithCount[] {
  const totalEyelets = NetLogic.calcEyelets(product).total
  if (totalEyelets === 0)
    return []
  const eyeletType = NetLogic.getEyeletType(product);
  if (!eyeletType)
    return [];
  return [
    { articleNumber: calcEyeletArticle(eyeletType), count: totalEyelets },
    { articleNumber: "KONFEYECUST", count: NetLogic.calcEyelets(product).totalCustom },
  ]
}

export class CalcNet3dLogic {
  static #addConfectionArticle(confectionArticles: Record<string, number>, articleNumber: string, edgeLength: number): void {
    confectionArticles[articleNumber] = (confectionArticles[articleNumber] || 0) + edgeLength;
  }

  static calcDimensionText(product: INetData, _lc: ILocaContext): string {
    switch (product.shapeType) {
      case "rectangle": return [product.dimensions.ab, product.dimensions.bc].map((v) => Measure.roundMM(v || 0)).join(" x ") + Measure.mm;
      case "triangle": return [product.dimensions.ab, product.dimensions.bc, product.dimensions.ac].map((v) => Measure.roundMM(v || 0)).join(" x ") + Measure.mm;
      case "quad": return [product.dimensions.ab, product.dimensions.bc, product.dimensions.cd, product.dimensions.ad, product.dimensions.ac].map((v) => Measure.roundMM(v || 0)).join(" x ") + Measure.mm;
    }
    return "Shape " + product.shapeType + " not supported!";
  }

  static calc3d(product: INetData, slc: ICalcContext): ICalculatedProductResult {

    const cornerPoints = NetGeometryHelper.calculateCornerPoints2d(product.dimensions, product.shapeType);
    if (cornerPoints.error) {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Rule_NetShapeNotPossible"));
    } else {
      for (const p of NetLogic.getPointNames(product)) {
        const angle = MathUtil.rad2deg(NetLogic.calcAngle(product, p));
        const minMaxAngle = NetLogic.getMinMaxAngleDeg();
        if (ltc(angle, minMaxAngle.min, 1)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, p, slc.lc.t("Rule_MinAngleNet"), {params: [minMaxAngle.min, angle.toFixed(1)], errorUrl: "https://info.soliday.eu/informationen-zur-vorhanghöhe-drahtseil"} );
        }
        if (gtc(angle, minMaxAngle.max, 1)) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, p, slc.lc.t("Rule_MaxAngleNet"), {params: [minMaxAngle.max, angle.toFixed(1)], errorUrl: "https://info.soliday.eu/informationen-zur-vorhanghöhe-drahtseil"});
        }
      }
    }

    const eyeletArticles = calculateEyeletArticles(product)
    for (const e of eyeletArticles) {
      if (e.count > 0)
        CalcProductLogic.addRuleArticle(slc, e.articleNumber, { count: e.count });
    }

    for (const p of NetLogic.getPointNames(product)) {
      const o = NetLogic.getCornerEyeletOverride(product, p);
      if (o === "dring") {
        CalcProductLogic.addRuleArticle(slc, "KONFEYEG16R", { count: 1 });
      }
    }

    const confectionArticles: Record<string, number> = {}
    const edgeNames = NetLogic.getEdgeNames(product);
    for (const edgeName of edgeNames) {
      const sideFinishingType = NetLogic.getSideFinishingType(product, edgeName);
      const edgeLength = product.dimensions[edgeName] || 0;
      switch (sideFinishingType) {
        case "cut": CalcNet3dLogic.#addConfectionArticle(confectionArticles, "KONFCUT0001", edgeLength); break;
        case "overlock": CalcNet3dLogic.#addConfectionArticle(confectionArticles, "KONFOVER001", edgeLength); break;
        case "narrow": CalcNet3dLogic.#addConfectionArticle(confectionArticles, "KONFNAHE001", edgeLength); break;
        case "hemstitch": CalcNet3dLogic.#addConfectionArticle(confectionArticles, NetLogic.getHemstitchSizeOption(product, edgeName) === "custom" ? "KONFHEMCUST" : "KONFHEMD025", edgeLength); break;
        case "pe": CalcNet3dLogic.#addConfectionArticle(confectionArticles, "KONFREPE001", edgeLength); break;
        case "profi": {
          let colorNumber = "01";
          switch (NetLogic.getEdgeColor(product, edgeName)) {
            case "black": colorNumber = "01"; break;
            case "grey": colorNumber = "02"; break;
            case "beige": colorNumber = "03"; break;
            case "white": colorNumber = "04"; break;
          }
          CalcNet3dLogic.#addConfectionArticle(confectionArticles, "KONFPROFI" + colorNumber, edgeLength);
          break;
        }
        case "keder": {
          const kederSize = NetLogic.getKederSize(product, edgeName);
          const sizeId = ("" + kederSize).replace(".", "").replace(",", "");
          CalcNet3dLogic.#addConfectionArticle(confectionArticles, `KONFPIPED${sizeId}`, edgeLength);

          const kederRail = NetLogic.getKederRail(product, edgeName);
          if (kederRail) {
            CalcProductLogic.addRuleArticle(slc, kederRail, {
              count: 1,
              mul: edgeLength,
              mulUnit: "m",
              partList: { list: "stockList" }
            });
          }
          break;
        }
        case "peVelcro": {
          const color = NetLogic.getVelcroColor(product, edgeName);
          const colorId = color === "black" ? "01" : "02";
          const front = NetLogic.getVelcroType(product, edgeName, "front");
          const back = NetLogic.getVelcroType(product, edgeName, "back");
          if (front) {
            CalcNet3dLogic.#addConfectionArticle(confectionArticles, `${front === "hook" ? "KONFHOOK" : "KONFLOOP"}F${colorId}`, edgeLength);
          }
          if (back) {
            CalcNet3dLogic.#addConfectionArticle(confectionArticles, `${front === "hook" ? "KONFHOOK" : "KONFLOOP"}B${colorId}`, edgeLength);
          }
          break;
        }
      }
    }

    for (const a of Object.keys(confectionArticles)) {
      const length = confectionArticles[a];
      CalcProductLogic.addRuleArticle(slc, a, {
        mul: length, stkUnit: "Stk", mulUnit: "m", count: 1,
      });
    }

    CalcProductLogic.addRuleArticle(slc, "spannFlex", { count: 0, articleType: "optional" }) // Spannflex schwarz
    CalcProductLogic.addRuleArticle(slc, "spannQuick", { count: 0, articleType: "optional" }) // Spannquick schwarz
    CalcProductLogic.addRuleArticle(slc, "rubberRope", { count: 0, articleType: "optional", idMode: "boxed" })
    CalcProductLogic.addRuleArticle(slc, "clipOnMini", { count: 0, articleType: "optional" }) // ClipOn mini white
    CalcProductLogic.addRuleArticle(slc, "00712A001", { count: 0, articleType: "optional" }) // Simplexhaken
    CalcProductLogic.addRuleArticle(slc, "00E13000234", { count: 0, articleType: "optional" }) // Befestigungsplatte - klein

    // CalcProductLogic.addRuleArticle(slc, "00E13001011", { count: 1 }); //Soliday Cleaner Oxidationreiniger
    // CalcProductLogic.addRuleArticle(slc, "00E13001011", { count: 1 }); //Soliday Cleaner Oxidationreiniger
    const cloth = NetLogic.getCloth(product);
    if (cloth) {
      const colorId = product.colorId;
      const baseArticleId = ClothData.getBaseArticleId(cloth);
      const area = NetLogic.calcArea(product);

      const maxArea = NetLogic.getMaxArea();
      if (gtc(area, maxArea, 2))
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "net", slc.lc.t("Rule_MaxNetArea"), { params: [maxArea, area.toFixed(2)]});

      CalcProductLogic.addRuleArticle(slc, baseArticleId, {
        mul: area, count: 1, stkUnit: "Stk", mulUnit: "m2",
        featureTemplate: appTemplates.netCloth,
        additionalArticleInfo: {
          dimensions: { header: slc.lc.t("ArticleInfo_Dimensions"), text: CalcNet3dLogic.calcDimensionText(product, slc.lc) }
        },
        features: {
          colorId,
          area,
          netShapeType: product.shapeType,
        },
        beosOrder: {
          anzahlCalculationType: "quadratMeter",
          einheit: "Stk",
        }
      })
    } else {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "net", "CLoth is null!");
    }

    CalcProductLogic.checkValidClothData(slc, PD.productToPD(product), FL.fromProduct(product), product.cloth, product.colorId);



    CalcProductLogic.finalizeCalcContext(slc, { incomplete: !product, defaultOfferList: "H", count: product.count });

    // if (product.shape === "triangle") {
    //   if (product.sides.ab! < 0.5)
    //     CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "AB", "Side AB is too small");
    // }

    // CalcProductLogic.addInfo(slc.infos, "Bla", "Info");

    return {
      id: product.id,
      pd: PD.productToPD(product),
      infos: slc.infos,
      errors: slc.errors,
      errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
      articles: slc.ruleArticles,
      // modified: product,
      product,
      // modifiedSunSail: sunSail,
    };
  }
}